import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { Button, Fade } from '@mui/material';

import { DropdownIcon, FeaturedShotsIcon } from '~/components/icons';
import { ImageContainer } from '~/components/ImageContainer';
import { ImageContextMenu } from '~/components/ImageContextMenu';
import { NavButton } from '~/components/NavButton';
import { useCanvas } from '~/contexts/CanvasContext';
import { useNotification } from '~/contexts/NotificationContext';
import { ECameraType } from '~/domain/ECameraType';
import { ICamera } from '~/domain/ICamera';
import { IImageSettings } from '~/domain/IImageSettings';
import { ResponseError } from '~/domain/ResponseError';
import { useContextMenu } from '~/hooks/useContextMenu';
import { useZoom } from '~/hooks/useFabric';
import { useImageQuery } from '~/hooks/useImage';
import { useRequestUrl } from '~/hooks/useRequestUrl';
import { useTooltipEventTracker } from '~/hooks/useTooltip';
import {
  ArrowLeftKey,
  ArrowRightKey,
  EndKey,
  HomeKey,
  isAnyModalOpened,
  isInputFocused,
} from '~/keyboard';
import * as LAYOUT from '~/pages/layout';
import { MakeURLFriendly } from '~/pages/urls';
import { useAppDispatch } from '~/redux';
import { addImageError } from '~/redux/features/imageErrorsSlice';

import { AnnotateContainer } from './AnnotateContainer';
import { AnnotationToolbar } from './AnnotationToolbar';
import { ImageAnnotation } from './ImageAnnotation';
import {
  DEALER_SHOT_PREVIEW_IMAGE_SETTINGS,
  FEATURE_SHOT_PREVIEW_IMAGE_SETTINGS,
} from './imageSettings';

interface IFeatureShotViewer {
  camera: ICamera;
  cameras: ICamera[];
  settings: IImageSettings;
  containerWidth: number;
}

const styles = {
  arrows: {
    position: 'absolute',
    top: '50%',
    zIndex: 1,
    '&:before': {
      background: 'rgba(76, 76, 77, 0.4)',
    },
  },
};

export const FeatureShotViewer: React.FC<IFeatureShotViewer> = (props) => {
  const { camera, cameras, containerWidth, settings } = props;
  const { aspectRatio } = settings;
  const canvasRef = useRef() as React.MutableRefObject<fabric.Canvas>;
  const notify = useNotification();
  const dispatch = useAppDispatch();
  const requestUrl = useRequestUrl();
  const url = requestUrl({
    camera: camera.type === ECameraType.FEATURE_RETAILER ? 'Retailer' : null,
  });
  const {
    isFetching,
    isError,
    isInitialLoading,
    data: src,
    refetch,
    error,
  } = useImageQuery({ settings, camera });

  //console.log('FeatureShotViewer containerWidth: ', containerWidth);

  const context = useCanvas();
  const [anchorPosition, contextMenu] = useContextMenu({ isFetching, isError });
  const navigate = useNavigate();
  const [showTooltip, events] = useTooltipEventTracker();
  const current = cameras.indexOf(camera);
  const homeUrl = requestUrl({ camera: MakeURLFriendly(cameras[0].name) });
  const endUrl = requestUrl({
    camera: MakeURLFriendly(cameras[cameras.length - 1].name),
  });
  const prevUrl =
    current > 0 &&
    requestUrl({ camera: MakeURLFriendly(cameras[current - 1].name) });
  const nextUrl =
    current < cameras.length - 1 &&
    requestUrl({ camera: MakeURLFriendly(cameras[current + 1].name) });
  const { annotationEnabled } = context;
  const showArrows = !annotationEnabled && !isFetching && showTooltip;
  const showPrevArrow = showArrows && !!prevUrl;
  const showNextArrow = showArrows && !!nextUrl;

  useZoom({
    ref: canvasRef,
    context,
  });

  useEffect(() => {
    const handleNavigation = (e: KeyboardEvent) => {
      const urls: Record<string, string | false> = {
        [HomeKey]: homeUrl,
        [EndKey]: endUrl,
        [ArrowRightKey]: nextUrl,
        [ArrowLeftKey]: prevUrl,
      };
      const url = urls[e.key];
      if (url && !isAnyModalOpened() && !isInputFocused()) {
        navigate(url, { replace: true });
      }
    };
    if (!annotationEnabled) {
      document.addEventListener('keydown', handleNavigation);
      return () => {
        document.removeEventListener('keydown', handleNavigation);
      };
    }
  }, [annotationEnabled, endUrl, homeUrl, navigate, nextUrl, prevUrl]);

  useEffect(() => {
    if (isError) {
      if (error) {
        const respError = error as ResponseError;
        dispatch(addImageError(respError.message));
      }
      notify({
        type: 'warning',
        message:
          'We couldn’t load this image. Please refresh or try again later',
      });
    }
  }, [isError, error, notify, dispatch]);

  return (
    <>
      <AnnotateContainer
        isAnnotating={annotationEnabled}
        containerWidth={containerWidth}
        onContextMenu={contextMenu.onContextMenu}
      >
        <ImageContainer
          aspectRatio={aspectRatio}
          isFetching={isFetching}
          isError={isError}
          isInitialLoading={isInitialLoading}
          refetch={refetch}
        >
          <ImageAnnotation
            ref={canvasRef}
            src={src}
            aspectRatio={aspectRatio}
            sx={LAYOUT.IMAGE}
            {...events}
          >
            <Fade in={showPrevArrow}>
              <NavButton
                component={RouterLink}
                to={prevUrl || ''}
                sx={[
                  styles.arrows,
                  {
                    transform: 'translateY(-50%) rotate(90deg)',
                    left: '20px',
                    visibility: showPrevArrow ? 'visible' : 'hidden',
                  },
                ]}
              >
                <DropdownIcon />
              </NavButton>
            </Fade>
            <Fade in={showNextArrow}>
              <NavButton
                component={RouterLink}
                to={nextUrl || ''}
                sx={[
                  styles.arrows,
                  {
                    transform: 'translateY(-50%) rotate(-90deg)',
                    right: '20px',
                    visibility: showNextArrow ? 'visible' : 'hidden',
                  },
                ]}
              >
                <DropdownIcon />
              </NavButton>
            </Fade>
          </ImageAnnotation>
        </ImageContainer>
        <AnnotationToolbar
          canvasRef={canvasRef}
          disabled={isError || isFetching}
        />
        {!annotationEnabled && (
          <Button
            startIcon={<FeaturedShotsIcon />}
            variant="icon"
            component={RouterLink}
            to={url + '?id=' + camera.code}
            sx={{ mt: 1 }}
          >
            Back to Grid View
          </Button>
        )}
      </AnnotateContainer>
      <ImageContextMenu
        anchorPosition={anchorPosition}
        context={contextMenu}
        camera={camera}
        images={[
          { label: 'HD', settings },
          {
            label: '4K',
            settings:
              camera.type === ECameraType.FEATURE_RETAILER
                ? DEALER_SHOT_PREVIEW_IMAGE_SETTINGS
                : FEATURE_SHOT_PREVIEW_IMAGE_SETTINGS,
          },
        ]}
      />
    </>
  );
};
